import React from 'react';

import {assert} from 'Asserts';
import * as benchlingUtil from 'BenchlingUtil';
import {SearchContext, SearchParams} from 'search/SearchConstants';
import {trackSelectedItem} from 'search/searchTracking';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipSearchInput, {
  ChipSearchInputSearch,
  NoResultOption,
  searchPost,
} from 'ui-library/inputs/ChipSearchInput';
import ItemOptionContent, {getIconIdForSearchResult} from 'ui-library/inputs/ItemOptionContent';
import {OptionData, OptionValue} from 'ui-library/menus/MenuConstants';
import APIUtil from 'util/APIUtil';
import {ElementKeyboardEvent, InputChangeEvent} from 'util/Types';

export interface Item {
  // The `id?` corresponds to BioentitySearchInput's selectedItems propType, as propTypes does not support
  // the only null type.
  id?: string | null; // Can be null if the item has been deleted
  name?: string | null; // Can be null if the item has been deleted
  isReadOnly?: boolean; // Set to true if this item can not be removed
}

export interface ItemSearchInputProps<TItem, TSearchResponse = unknown> {
  // In normal use, chips are not clickable links while the input control is in use. You can override that here,
  // to make chips clickable at all times.
  areChipsClickable?: boolean;
  autoFocus?: boolean;
  chipSize?: ChipSize;
  className?: string;
  disabled: boolean;
  doSearch: ChipSearchInputSearch;
  emptyQueryMessage?: string;
  fetchParams?:
    | {
        query?: string;
        limit?: number;
        section?: string;
        filter?: string;
        context?: SearchContext;
      }
    // Passed by StorableSearchInput
    | Omit<SearchParams, 'limit' | 'query'>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getExtraFromResponse?: (searchResponse: any, itemId: string) => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getOptionDatasFromResponse?: (searchResponse: any) => Array<OptionData>;
  initialQuery: string;
  isReadOnly: boolean;
  itemKey: string;
  onBlur?: () => void;
  onKeyDown?: (e: ElementKeyboardEvent) => void;
  onItemChange?: (selectedItem: TItem, isSelected: boolean) => void;
  onChange: (selectedItems: Array<TItem>) => void;
  placeholder: string;
  searchOnEmptyQuery: boolean;
  searchURL: string;
  selectedItems: Array<TItem>; // `id` may be null if item has been deleted
  shouldAllowMultiple: boolean;
  hideSelectedResults: boolean;
  summarize: boolean;
  renderChips: null | ((values: Array<OptionValue>) => JSX.Element | null);
  noResultsOptions?: Array<NoResultOption> | null;
  onSearchStart?: () => void;
  onSearchSuccess?: (resp: TSearchResponse) => void;

  // This component can be controlled. See additional comments in getInitialState() below
  query?: string;
  onUpdateQuery?: (query: string) => void;

  renderOptionContent?: (optionData: OptionData) => JSX.Element;

  // This prop is passed to `ChipSearchInput` as `key`. It exists to allow upstream components to trigger a
  // re-query based on info that isn't otherwise passed through to `ChipSearchInput` as props. See also the
  // comment on the deps list on ChipSearchInput's `useEffect`.
  chipSearchInputKey?: string | number;
}

interface State {
  query: string | undefined;
  isControlled: boolean;
}

/*
 * A search input component that uses ChipSearchInput.
 */
class ItemSearchInput<TItem extends Item, TSearchResponse = unknown> extends React.Component<
  ItemSearchInputProps<TItem, TSearchResponse>,
  State
> {
  static defaultProps = {
    autoFocus: true,
    chipSize: ChipSize.SMALL,
    disabled: false,
    doSearch: searchPost,
    initialQuery: '',
    isReadOnly: false,
    placeholder: 'Search',
    searchOnEmptyQuery: false,
    shouldAllowMultiple: false,
    hideSelectedResults: false,
    summarize: false,
    renderChips: null,
  };

  state: State = {
    query: this.props.initialQuery,
    // Note: this component can be controlled, but it cannot be changed from uncontrolled
    // to controlled! It makes its determination based on your initial set of props.
    isControlled: Boolean(this.props.query != null && this.props.onUpdateQuery),
  };

  static getDerivedStateFromProps(props: ItemSearchInputProps<Item>, state: State): Partial<State> | null {
    if (state.isControlled && props.query != null) {
      return {query: props.query};
    }
    return null;
  }

  private canSelectAdditional(): boolean {
    return (
      !this.props.isReadOnly && (this.props.selectedItems.length === 0 || this.props.shouldAllowMultiple)
    );
  }

  private handleInputChange = (e: InputChangeEvent): void => {
    if (!this.canSelectAdditional()) {
      // Block typing if can't select additional
      return;
    }
    this.setQuery(e.target.value);
  };

  private handleRemove = (itemToRemove: TItem): void => {
    assert(!this.props.isReadOnly, 'Unexpectedly tried to remove item from read-only ItemSearchInput.');
    // This no-op can occur if the user types backspace after double-clicking an ItemSearchInput to edit it.
    if (itemToRemove.isReadOnly) {
      return;
    }
    this.setQuery('');
    if (this.props.onItemChange) {
      this.props.onItemChange(itemToRemove, false);
    }
    this.props.onChange(this.props.selectedItems.filter((item) => item !== itemToRemove));
  };

  // Used in ParameterValueTemplateFieldHarness :-\
  handleSelectAttachment(id: string, name: string, url: string, item: TItem): void {
    // Reset the query before calling onChange, rather than after. This avoids clobbering the selected item in
    // component trees where the same state is used for both the selected items and the query. (e.g., an entry
    // link field in a structured table may store both in the cell's formula.)
    this.setQuery('');
    this.props.onItemChange?.(item, true);
    this.props.onChange([...this.props.selectedItems, item]);
  }

  private setQuery(query: string): void {
    if (this.state.isControlled && this.props.onUpdateQuery) {
      this.props.onUpdateQuery(query);
    } else {
      this.setState({query});
    }
  }

  private getOptionDatasFromResponse = (resp: {[itemKey: string]: Array<TItem>}): Array<OptionData> => {
    const idsToIgnore = this.props.hideSelectedResults ? this.props.selectedItems.map((item) => item.id) : [];
    const filteredItems = resp[this.props.itemKey].filter(
      (item: TItem) => !idsToIgnore.includes(item.id)
    ) as Array<{id: string; name: string; creator?: string}>;
    return filteredItems.map((item) => {
      let extra;
      if (this.props.getExtraFromResponse) {
        extra = this.props.getExtraFromResponse(resp, item.id);
      }
      // TODO(Rachel): Simplify this when no longer relying on AttachDropdownOptionContainer.
      const value = {
        extra,
        item,
        item_id: item.id,
        name: item.name,
        type: APIUtil.apiIdToType(item.id),
        // Conditionally include creator property if it exists in the item
        ...(item.creator && {creator: item.creator}),
      };
      return {
        name: item.name,
        icon: getIconIdForSearchResult(value),
        value,
      };
    });
  };

  private renderOptionContent = (optionData: OptionData): JSX.Element => (
    <ItemOptionContent optionData={optionData} />
  );

  render(): JSX.Element {
    return (
      <ChipSearchInput<TSearchResponse>
        autoFocus={this.props.autoFocus}
        className={this.props.className}
        disabled={this.props.disabled}
        emptyQueryMessage={this.props.emptyQueryMessage}
        chipClass={BenchChip}
        getChipProps={(optionValue) => ({
          id: optionValue.id,
          isReadOnly: optionValue.isReadOnly,
          isLink: Boolean(this.props.areChipsClickable),
          size: this.props.chipSize,
        })}
        key={this.props.chipSearchInputKey}
        getOptionDatasFromResponse={this.props.getOptionDatasFromResponse || this.getOptionDatasFromResponse}
        isMulti={this.props.shouldAllowMultiple}
        isReadOnly={this.props.isReadOnly}
        onBlur={this.props.onBlur}
        onKeyDown={this.props.onKeyDown}
        onQueryChange={this.handleInputChange}
        onRemove={this.handleRemove}
        noResultsOptions={this.props.noResultsOptions}
        onSelect={(value: OptionValue) => {
          if (value != null) {
            trackSelectedItem(value.item_id, value._searchPosition, value._searchCorrelationId);
            this.handleSelectAttachment(
              value.item_id,
              value.name,
              benchlingUtil.pathToURL(value.item.editURL),
              value.item
            );
          }
        }}
        placeholder={this.props.placeholder}
        query={this.state.query}
        renderOptionContent={this.props.renderOptionContent ?? this.renderOptionContent}
        searchOnEmptyQuery={this.props.searchOnEmptyQuery}
        searchURL={this.props.searchURL}
        fetchParams={{limit: 10, ...this.props.fetchParams}}
        doSearch={this.props.doSearch}
        value={this.props.selectedItems}
        renderChips={this.props.renderChips}
        onSearchStart={this.props.onSearchStart}
        onSearchSuccess={this.props.onSearchSuccess}
      />
    );
  }
}

export default ItemSearchInput;
